import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CircleHelp,
  Loader2,
  Mail,
  Mic,
  MicOff,
  PenLine,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  cancelAiProposal,
  clarifyAiProposal,
  createAiSession,
  decideAiBatch,
  fetchAiAskCatalogue,
  fetchAiSystemSettings,
  fetchOpportunity,
  postAiAsk,
  postChatTurn,
  selectAiProposalOpportunity,
  undoAiProposal,
} from '@/api/client'
import type {
  AiAskCatalogueQuestion,
  AiBatchPayload,
  AiProposalCard,
  ChatTurnResponse,
} from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { IconButton } from '@/components/IconButton'
import { PageHelp } from '@/components/PageHelp'
import { useToast } from '@/components/toast-context'
import { MarkdownContent } from './MarkdownContent'
import { usePanelDrag } from './usePanelDrag'
import { useSpeechDictation } from './useSpeechDictation'

type ChatMode = 'mutate' | 'ask'

type ThreadItem =
  | {
      id: string
      kind: 'user'
      message: string
      attachmentChars?: number
      mode: ChatMode
    }
  | {
      id: string
      kind: 'ask'
      answer: string
      supported?: AiAskCatalogueQuestion[]
      toolTrace?: Array<{ tool?: string; ok?: boolean }>
    }
  | {
      id: string
      kind: 'system'
      message: string
      retryable?: boolean
      formFallback?: boolean
    }
  | { id: string; kind: 'batch'; batch: AiBatchPayload }

interface Props {
  open: boolean
  onClose: () => void
  /** §4.1 — open opportunity detail in the hub (Open full form). */
  onOpenOpportunity?: (opportunityId: string) => void
  /** Notify host that a confirm landed so lists can refetch. */
  onConfirmed?: () => void
  /** Host badge when a turn completes (panel may be collapsed). */
  onActivityDone?: () => void
}

function AiFieldBadge() {
  return (
    <span className="flag-chip flag-chip--info" title="Suggested by AI">
      AI
    </span>
  )
}

function evidenceText(card: AiProposalCard): string[] {
  const rows = card.evidence ?? []
  return rows
    .map((row) => {
      if (typeof row === 'string') return row
      if (row && typeof row === 'object' && typeof row.text === 'string') return row.text
      return ''
    })
    .filter(Boolean)
}

function frictionClass(friction?: string | null): string {
  if (friction === 'hard_blocked') return 'ai-assist__card--danger'
  if (friction === 'needs_op_ack' || friction === 'needs_batch_ack') return 'ai-assist__card--warn'
  return ''
}

export function ChatPanel({
  open,
  onClose,
  onOpenOpportunity,
  onConfirmed,
  onActivityDone,
}: Props) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const [mode, setMode] = useState<ChatMode>('ask')
  const [emailPasteOpen, setEmailPasteOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [attachmentText, setAttachmentText] = useState('')
  const [pastedRanges, setPastedRanges] = useState<Array<{ start: number; end: number }>>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [thread, setThread] = useState<ThreadItem[]>([])
  const [ackedKeys, setAckedKeys] = useState<Record<string, Set<string>>>({})
  const [clarifyDrafts, setClarifyDrafts] = useState<Record<string, Record<string, string>>>({})
  const [checkAcks, setCheckAcks] = useState<Record<string, Set<string>>>({})
  const [expandedEvidence, setExpandedEvidence] = useState<Record<string, boolean>>({})
  const [lastPayload, setLastPayload] = useState<{
    mode: ChatMode
    message: string
    attachment: string
    queryId?: string
  } | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['ai', 'settings', 'system'],
    queryFn: fetchAiSystemSettings,
    enabled: open,
  })

  const catalogueQuery = useQuery({
    queryKey: ['ai', 'ask', 'catalogue'],
    queryFn: fetchAiAskCatalogue,
    enabled: open && mode === 'ask',
    staleTime: 60_000,
  })

  const pasteMax = settingsQuery.data?.paste_trivial_max ?? 40
  const messageMax = settingsQuery.data?.message_max_chars ?? 500
  const aiEnabled = Boolean(settingsQuery.data?.ai_enabled)
  const chatOn = Boolean(settingsQuery.data?.channels?.chat)
  const askOn = Boolean(settingsQuery.data?.channels?.ask)
  const askFreeform = Boolean(catalogueQuery.data?.ask_freeform)
  const gatedOff = settingsQuery.isSuccess && (!aiEnabled || (mode === 'mutate' ? !chatOn : !askOn))

  const dictation = useSpeechDictation({
    active: open && !gatedOff,
    maxLength: messageMax,
    onFinal: setMessage,
    onError: (msg) => toast.error(msg),
  })
  const composerDisplay = dictation.previewMessage ?? message
  const { offset, dragged, headerDragHandlers } = usePanelDrag(open)

  useEffect(() => {
    if (!open) return
    if (sessionId) return
    void createAiSession()
      .then((s) => setSessionId(s.id))
      .catch((err: Error) => toast.error(err.message))
  }, [open, sessionId, toast])

  useEffect(() => {
    if (!open) return
    const node = panelRef.current
    // Host restores focus to the toolbar entry on close — do not steal it back here.
    composerRef.current?.focus()
    const onKey = (event: Event) => {
      const ke = event as unknown as globalThis.KeyboardEvent
      if (ke.key === 'Escape') {
        ke.preventDefault()
        onClose()
        return
      }
      if (ke.key !== 'Tab' || !node) return
      const focusable = node.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (ke.shiftKey && document.activeElement === first) {
        ke.preventDefault()
        last.focus()
      } else if (!ke.shiftKey && document.activeElement === last) {
        ke.preventDefault()
        first.focus()
      }
    }
    node?.addEventListener('keydown', onKey)
    return () => {
      node?.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const replaceBatchInThread = useCallback((batch: AiBatchPayload) => {
    setThread((prev) => {
      const next = [...prev]
      for (let i = 0; i < next.length; i += 1) {
        const item = next[i]
        if (item?.kind === 'batch' && item.batch.id === batch.id) {
          next[i] = { id: item.id, kind: 'batch', batch }
          return next
        }
      }
      return [...next, { id: `batch-${batch.id}`, kind: 'batch', batch }]
    })
  }, [])

  const appendTurnResult = useCallback(
    (data: ChatTurnResponse, meta: { mode: ChatMode; message: string; attachment: string }) => {
      const id = data.turn_id ?? `t-${Date.now()}`
      const additions: ThreadItem[] = [
        {
          id: `${id}-user`,
          kind: 'user',
          message: meta.message,
          attachmentChars: meta.attachment ? meta.attachment.length : undefined,
          mode: meta.mode,
        },
      ]
      if (data.batch) {
        additions.push({ id: `${id}-batch`, kind: 'batch', batch: data.batch })
      } else if (data.answer) {
        additions.push({
          id: `${id}-ask`,
          kind: 'ask',
          answer: data.answer,
          supported: data.supported_questions ?? undefined,
          toolTrace: data.tool_trace ?? undefined,
        })
      } else if (data.supported_questions?.length) {
        additions.push({
          id: `${id}-ask`,
          kind: 'ask',
          answer: 'Choose a supported question:',
          supported: data.supported_questions,
        })
      } else if (data.message) {
        additions.push({
          id: `${id}-sys`,
          kind: 'system',
          message: data.message,
          retryable: Boolean(data.retryable),
          formFallback: Boolean(data.form_fallback),
        })
      }
      setThread((prev) => [...prev, ...additions])
    },
    [],
  )

  const turnMutation = useMutation({
    mutationFn: async (opts?: { queryId?: string; messageOverride?: string }) => {
      const msg = (opts?.messageOverride ?? message).trim()
      const attachment = attachmentText.trim()
      if (mode === 'ask') {
        return {
          data: await postAiAsk({
            message: opts?.queryId ? undefined : msg || undefined,
            query_id: opts?.queryId,
            session_id: sessionId,
          }),
          meta: { mode, message: msg || opts?.queryId || '', attachment: '' } as const,
        }
      }
      return {
        data: await postChatTurn({
          session_id: sessionId,
          message: msg,
          attachment_text: attachment || null,
          mode: 'mutate',
          pasted_ranges: pastedRanges.length ? pastedRanges : undefined,
        }),
        meta: { mode, message: msg, attachment } as const,
      }
    },
    onSuccess: ({ data, meta }) => {
      setLastPayload({
        mode: meta.mode,
        message: meta.message,
        attachment: meta.attachment,
      })
      appendTurnResult(data, meta)
      setMessage('')
      setAttachmentText('')
      setPastedRanges([])
      // Guidance replies use `message` too — only toast hard failures.
      if (
        data.outcome === 'failed' &&
        data.message &&
        !data.batch &&
        !data.answer &&
        !data.supported_questions?.length
      ) {
        toast.error(data.message)
      }
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onMessagePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const text = event.clipboardData.getData('text')
      if (text.length >= pasteMax || text.includes('\n')) {
        event.preventDefault()
        setAttachmentText((prev) => (prev ? `${prev}\n\n${text}` : text))
        setMessage('')
        setPastedRanges([])
        toast.push('Moved pasted text to email attachment area.')
      } else {
        const start = event.currentTarget.selectionStart ?? message.length
        const end = start + text.length
        setPastedRanges((prev) => [...prev, { start, end }])
      }
    },
    [message.length, pasteMax, toast],
  )

  const toggleAck = (cardId: string, key: string) => {
    setAckedKeys((prev) => {
      const next = new Set(prev[cardId] ?? [])
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return { ...prev, [cardId]: next }
    })
  }

  const decide = async (
    batch: AiBatchPayload,
    card: AiProposalCard,
    decision: 'accept' | 'skip',
  ) => {
    const required = card.op_acks ?? []
    const provided =
      decision === 'accept' && required.length > 0
        ? Array.from(ackedKeys[card.id] ?? [])
        : undefined
    try {
      const updated = await decideAiBatch(batch.id, [
        { proposal_id: card.id, decision, op_acks: provided },
      ])
      replaceBatchInThread({
        id: updated.id,
        soft_limit: updated.soft_limit ?? batch.soft_limit,
        hard_cap: updated.hard_cap ?? batch.hard_cap,
        cards: updated.cards,
      })
      if (decision === 'accept') {
        const nextCard = updated.cards.find((c) => c.id === card.id)
        if (nextCard?.status === 'failed') {
          toast.error(
            nextCard.failure_message?.trim() ||
              'Confirm failed — open the opportunity form to apply the change.',
          )
        } else if (nextCard?.status === 'confirmed') {
          toast.success('Confirmed AI suggestion.')
          void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
          onConfirmed?.()
          onActivityDone?.()
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Decide failed.')
    }
  }

  const patchCardInBatch = (batch: AiBatchPayload, updated: AiProposalCard) => {
    replaceBatchInThread({
      ...batch,
      cards: batch.cards.map((c) => (c.id === updated.id ? updated : c)),
    })
  }

  const applyClarify = async (batch: AiBatchPayload, card: AiProposalCard) => {
    const fields = clarifyDrafts[card.id] ?? {}
    const checks = Array.from(checkAcks[card.id] ?? [])
    try {
      const updated = await clarifyAiProposal(card.id, {
        fields,
        checks_acknowledged: checks,
      })
      patchCardInBatch(batch, updated)
      toast.success('Clarifications applied.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Clarify failed.')
    }
  }

  const pickOpportunity = async (
    batch: AiBatchPayload,
    card: AiProposalCard,
    opportunityId: string,
  ) => {
    try {
      const updated = await selectAiProposalOpportunity(card.id, opportunityId)
      patchCardInBatch(batch, updated)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not select opportunity.')
    }
  }

  const undoCard = async (batch: AiBatchPayload, card: AiProposalCard) => {
    try {
      const updated = await undoAiProposal(card.id)
      patchCardInBatch(batch, updated)
      toast.success('Undid AI confirm.')
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      onConfirmed?.()
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Undo failed. Closed months and newer versions cannot be undone here.',
      )
    }
  }

  const clearChat = () => {
    dictation.stop()
    setSessionId(null)
    setThread([])
    setAckedKeys({})
    setClarifyDrafts({})
    setCheckAcks({})
    setExpandedEvidence({})
    setLastPayload(null)
    setMessage('')
    setAttachmentText('')
    setPastedRanges([])
    setMode('ask')
    setEmailPasteOpen(false)
  }

  const canClearChat =
    thread.length > 0 || Boolean(message.trim()) || Boolean(attachmentText.trim())

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (!turnMutation.isPending && (message.trim() || attachmentText.trim() || mode === 'ask')) {
        dictation.stop()
        turnMutation.mutate({})
      }
    }
  }

  const adjustComposerHeight = useCallback(() => {
    const el = composerRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [])

  useEffect(() => {
    adjustComposerHeight()
  }, [composerDisplay, adjustComposerHeight])

  if (!open) return null

  return (
    <aside
      ref={panelRef}
      className={`ai-assist${dragged ? ' ai-assist--dragged' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={
        offset
          ? {
              left: offset.x,
              top: offset.y,
              right: 'auto',
            }
          : undefined
      }
    >
      <header className="ai-assist__header" title="Drag to move" {...headerDragHandlers}>
        <div className="ai-assist__title-row">
          <Sparkles size={18} strokeWidth={2} aria-hidden />
          <h2 id={titleId}>AI assist</h2>
        </div>
        <div className="ai-assist__header-actions">
          <PageHelp helpKey="ai-assist" />
          <IconButton
            label="Clear chat"
            variant="ghost"
            disabled={!canClearChat}
            onClick={clearChat}
          >
            <Trash2 size={18} strokeWidth={2} />
          </IconButton>
          <IconButton label="Close AI assist" variant="ghost" onClick={onClose}>
            <X size={18} strokeWidth={2} />
          </IconButton>
        </div>
      </header>

      {gatedOff ? (
        <div className="ai-assist__body">
          <EmptyState
            title="AI assist is turned off by System admin."
            description={
              !aiEnabled
                ? 'The kill switch is on — AI features are disabled.'
                : mode === 'ask'
                  ? 'The Ask channel is disabled.'
                  : 'The chat channel is disabled.'
            }
          />
        </div>
      ) : (
        <>
          {mode === 'ask' && (catalogueQuery.data?.questions?.length ?? 0) > 0 ? (
            <div className="ai-assist__chips" aria-label="Suggested ask questions">
              {catalogueQuery.data!.questions.map((q) => (
                <button
                  key={q.query_id}
                  type="button"
                  className="button button--secondary ai-assist__chip"
                  disabled={turnMutation.isPending}
                  onClick={() => turnMutation.mutate({ queryId: q.query_id })}
                >
                  {q.label}
                </button>
              ))}
            </div>
          ) : null}
          {mode === 'ask' && askFreeform ? (
            <p className="ai-assist__hint" role="note">
              You can also ask in your own words (read-only tools).
            </p>
          ) : null}

          <div className="ai-assist__thread">
            {thread.length === 0 && !turnMutation.isPending ? (
              <EmptyState
                title={
                  mode === 'ask'
                    ? askFreeform
                      ? 'Ask a question'
                      : 'Ask a catalogue question'
                    : 'Describe an update'
                }
                description={
                  mode === 'ask'
                    ? askFreeform
                      ? 'Use a suggestion chip or type a freeform question about customers, forecast, or pipeline.'
                      : 'Use a suggestion chip or type a supported question.'
                    : emailPasteOpen || attachmentText
                      ? 'Paste email text with the mail icon, then type what to change.'
                      : 'Type an instruction. Use the mail icon to paste an email.'
                }
              />
            ) : null}

            {turnMutation.isPending ? (
              <p className="ai-assist__progress" role="status">
                <Loader2 className="ai-assist__spin" size={16} aria-hidden /> Working…
              </p>
            ) : null}

            {thread.map((item) => {
              if (item.kind === 'user') {
                return (
                  <div key={item.id} className="ai-assist__bubble ai-assist__bubble--user">
                    <p>{item.message || '(attachment only)'}</p>
                    {item.attachmentChars ? (
                      <span className="flag-chip">{item.attachmentChars} chars pasted</span>
                    ) : null}
                  </div>
                )
              }
              if (item.kind === 'ask') {
                return (
                  <div
                    key={item.id}
                    className="ai-assist__bubble ai-assist__bubble--assistant"
                    role="status"
                  >
                    <MarkdownContent className="ai-assist__answer">{item.answer}</MarkdownContent>
                    {item.toolTrace?.length ? (
                      <details className="ai-assist__sources">
                        <summary>Sources used</summary>
                        <ul>
                          {item.toolTrace.map((row, idx) => (
                            <li key={`${row.tool ?? 'tool'}-${idx}`}>
                              <code>{row.tool ?? 'tool'}</code>
                              {row.ok === false ? ' (failed)' : ''}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                    {item.supported?.length ? (
                      <div className="ai-assist__supported-block">
                        <p className="ai-assist__supported-label">Suggestion chips</p>
                        <ul className="ai-assist__supported">
                          {item.supported.map((q) => (
                            <li key={q.query_id}>
                              <button
                                type="button"
                                className="button button--secondary ai-assist__chip"
                                onClick={() => turnMutation.mutate({ queryId: q.query_id })}
                              >
                                {q.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )
              }
              if (item.kind === 'system') {
                return (
                  <div key={item.id} className="ai-assist__callout" role="status">
                    <MarkdownContent>{item.message}</MarkdownContent>
                    <div className="ai-assist__card-actions">
                      {item.retryable && lastPayload ? (
                        <button
                          type="button"
                          className="button button--secondary"
                          onClick={() => {
                            setMode(lastPayload.mode)
                            setMessage(lastPayload.message)
                            setAttachmentText(lastPayload.attachment)
                            turnMutation.mutate({})
                          }}
                        >
                          Retry
                        </button>
                      ) : null}
                      {item.formFallback ? (
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() => {
                            onClose()
                            toast.push('Continue in the opportunity form.')
                          }}
                        >
                          Use form
                        </button>
                      ) : null}
                    </div>
                  </div>
                )
              }
              const batch = item.batch
              return (
                <div key={item.id} className="ai-assist__batch">
                  {batch.cards.length > batch.soft_limit ? (
                    <p className="ai-assist__cap-banner" role="status">
                      Soft cap {batch.soft_limit}: {batch.cards.length - batch.soft_limit} card(s)
                      over — accept per card (no accept-all). Hard cap {batch.hard_cap}.
                    </p>
                  ) : null}
                  {(() => {
                    const readyCount = batch.cards.filter(
                      (c) => c.status === 'ready' || c.status === 'draft',
                    ).length
                    return readyCount > 0 ? (
                      <p className="ai-assist__impact" role="status">
                        {readyCount} card(s) ready to confirm (per-card only — no accept-all).
                        Combined impact preview lands with S-2A-014.
                      </p>
                    ) : null
                  })()}
                  <ul className="ai-assist__cards">
                    {batch.cards.map((card) => (
                      <ProposalCardView
                        key={card.id}
                        card={card}
                        acked={ackedKeys[card.id] ?? new Set()}
                        onToggleAck={(key) => toggleAck(card.id, key)}
                        clarifyDraft={clarifyDrafts[card.id] ?? {}}
                        onClarifyChange={(field, value) =>
                          setClarifyDrafts((prev) => ({
                            ...prev,
                            [card.id]: { ...(prev[card.id] ?? {}), [field]: value },
                          }))
                        }
                        checkAcks={checkAcks[card.id] ?? new Set()}
                        onToggleCheck={(itemCheck) =>
                          setCheckAcks((prev) => {
                            const next = new Set(prev[card.id] ?? [])
                            if (next.has(itemCheck)) next.delete(itemCheck)
                            else next.add(itemCheck)
                            return { ...prev, [card.id]: next }
                          })
                        }
                        evidenceOpen={Boolean(expandedEvidence[card.id])}
                        onToggleEvidence={() =>
                          setExpandedEvidence((prev) => ({
                            ...prev,
                            [card.id]: !prev[card.id],
                          }))
                        }
                        onDecide={(d) => void decide(batch, card, d)}
                        onCancel={() =>
                          void cancelAiProposal(card.id).then((updated) => {
                            patchCardInBatch(batch, updated)
                          })
                        }
                        onClarify={() => void applyClarify(batch, card)}
                        onPickOpportunity={(id) => void pickOpportunity(batch, card, id)}
                        onOpenForm={() => {
                          if (card.opportunity_id) onOpenOpportunity?.(card.opportunity_id)
                          else toast.push('Pick or match an opportunity first.')
                        }}
                        onUndo={() => void undoCard(batch, card)}
                      />
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          <form
            className="ai-assist__composer"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              dictation.stop()
              turnMutation.mutate({})
            }}
          >
            <div className="ai-assist__composer-bar">
              <div className="ai-assist__mode-icons" role="toolbar" aria-label="AI mode">
                <IconButton
                  label="Ask mode"
                  variant="ghost"
                  active={mode === 'ask'}
                  aria-pressed={mode === 'ask'}
                  onClick={() => {
                    setMode('ask')
                    setEmailPasteOpen(false)
                  }}
                >
                  <CircleHelp size={16} strokeWidth={2} />
                </IconButton>
                <IconButton
                  label="Update mode"
                  variant="ghost"
                  active={mode === 'mutate' && !emailPasteOpen}
                  aria-pressed={mode === 'mutate' && !emailPasteOpen}
                  onClick={() => {
                    setMode('mutate')
                    setEmailPasteOpen(false)
                  }}
                >
                  <PenLine size={16} strokeWidth={2} />
                </IconButton>
                <IconButton
                  label="Paste email"
                  variant="ghost"
                  active={mode === 'mutate' && emailPasteOpen}
                  aria-pressed={mode === 'mutate' && emailPasteOpen}
                  onClick={() => {
                    setMode('mutate')
                    setEmailPasteOpen((openPaste) => (mode === 'mutate' ? !openPaste : true))
                  }}
                >
                  <Mail size={16} strokeWidth={2} />
                </IconButton>
                <IconButton
                  label={
                    dictation.listening
                      ? 'Stop dictation'
                      : dictation.supported
                        ? 'Dictate with microphone'
                        : 'Dictation needs Chrome or Edge'
                  }
                  variant="ghost"
                  active={dictation.listening}
                  aria-pressed={dictation.listening}
                  className={dictation.listening ? 'ai-assist__mic--live' : undefined}
                  disabled={turnMutation.isPending || gatedOff || !dictation.supported}
                  onClick={() => dictation.toggle(message)}
                >
                  {dictation.listening ? (
                    <MicOff size={16} strokeWidth={2} />
                  ) : (
                    <Mic size={16} strokeWidth={2} />
                  )}
                </IconButton>
              </div>
              <button
                type="submit"
                className="button button--primary ai-assist__send"
                disabled={
                  turnMutation.isPending ||
                  (mode === 'mutate' && !message.trim() && !attachmentText.trim())
                }
              >
                {turnMutation.isPending ? (
                  <Loader2 className="ai-assist__spin" size={16} aria-hidden />
                ) : (
                  <Send size={16} aria-hidden />
                )}
                Send
              </button>
            </div>

            <label className="ai-assist__label visually-hidden" htmlFor={`${titleId}-message`}>
              Message
            </label>
            <textarea
              id={`${titleId}-message`}
              ref={composerRef}
              className={`ai-assist__input${dictation.listening ? ' ai-assist__input--dictating' : ''}`}
              rows={2}
              value={composerDisplay}
              maxLength={messageMax}
              onChange={(e) => {
                if (dictation.listening) dictation.stop()
                setMessage(e.target.value)
              }}
              onPaste={onMessagePaste}
              onKeyDown={onComposerKeyDown}
              placeholder={
                dictation.listening
                  ? 'Listening… speak your update'
                  : mode === 'ask'
                    ? askFreeform
                      ? 'Ask in your own words…'
                      : 'Ask a catalogue question…'
                    : 'Type an update instruction…'
              }
              aria-describedby={dictation.listening ? `${titleId}-dictating` : undefined}
            />
            {dictation.listening ? (
              <span id={`${titleId}-dictating`} className="ai-assist__dictating" role="status">
                Listening — click the mic again to stop
              </span>
            ) : null}
            <span className="ai-assist__counter">
              {composerDisplay.length}/{messageMax}
            </span>

            {mode === 'mutate' && (emailPasteOpen || Boolean(attachmentText)) ? (
              <label className="ai-assist__label ai-assist__label--untrusted">
                Email paste
                <textarea
                  className="ai-assist__input"
                  rows={attachmentText ? 4 : 2}
                  value={attachmentText}
                  onChange={(e) => setAttachmentText(e.target.value)}
                  placeholder="Paste email content here…"
                />
                {attachmentText ? (
                  <span className="ai-assist__counter">{attachmentText.length} chars</span>
                ) : null}
              </label>
            ) : null}
          </form>
        </>
      )}
    </aside>
  )
}

function ProposalCardView({
  card,
  acked,
  onToggleAck,
  clarifyDraft,
  onClarifyChange,
  checkAcks,
  onToggleCheck,
  evidenceOpen,
  onToggleEvidence,
  onDecide,
  onCancel,
  onClarify,
  onPickOpportunity,
  onOpenForm,
  onUndo,
}: {
  card: AiProposalCard
  acked: Set<string>
  onToggleAck: (key: string) => void
  clarifyDraft: Record<string, string>
  onClarifyChange: (field: string, value: string) => void
  checkAcks: Set<string>
  onToggleCheck: (item: string) => void
  evidenceOpen: boolean
  onToggleEvidence: () => void
  onDecide: (d: 'accept' | 'skip') => void
  onCancel: () => void
  onClarify: () => void
  onPickOpportunity: (id: string) => void
  onOpenForm: () => void
  onUndo: () => void
}) {
  const missing = card.missing_fields ?? []
  const checks = card.needs_user_check ?? []
  const candidates = card.candidate_opportunity_ids ?? []
  const needsMini = missing.length > 0 || checks.length > 0
  const needsPick = candidates.length > 0 && !card.opportunity_id
  const requiredAcks = card.op_acks ?? []
  const needsAck = card.friction === 'needs_op_ack' || card.friction === 'needs_batch_ack'
  const ackComplete = !needsAck || requiredAcks.every((k) => acked.has(k))
  const quotes = evidenceText(card)
  const confirmed = card.status === 'confirmed'
  const failed = card.status === 'failed'
  const createBlocked = card.action === 'create'
  const failureMessage =
    card.failure_message?.trim() ||
    (Array.isArray(card.assumptions)
      ? card.assumptions
          .map((row) =>
            row && typeof row === 'object' && 'confirm_error' in row
              ? String((row as { confirm_error?: unknown }).confirm_error ?? '')
              : '',
          )
          .find((text) => text.trim())
      : undefined)

  return (
    <li
      className={`ai-assist__card ${frictionClass(card.friction)}${failed ? ' ai-assist__card--danger' : ''}`.trim()}
    >
      <div className="ai-assist__card-head">
        <strong>{card.action.toUpperCase()}</strong>
        {card.source_label ? <span className="flag-chip">{card.source_label}</span> : null}
        <span className={`flag-chip ai-assist__status ai-assist__status--${card.status}`}>
          {card.status}
        </span>
      </div>

      {failed ? (
        <p className="ai-assist__mini-lede" role="alert">
          {failureMessage ||
            'Confirm could not apply this change. Use Open full form, or Cancel and try again.'}
        </p>
      ) : null}

      {createBlocked ? (
        <p className="ai-assist__mini-lede">
          Create proposals finish in the opportunity form — Confirm here stays blocked.
        </p>
      ) : null}

      {card.friction === 'hard_blocked' ? (
        <p className="ai-assist__mini-lede">
          Hard-blocked until you name the opportunity in your message (or pick a candidate) so lift
          can apply.
        </p>
      ) : null}

      <ul className="ai-assist__fields">
        {(card.resolved_fields ?? []).map((row, idx) => (
          <li key={`${card.id}-${idx}`}>
            <span>{row.field}</span>:{' '}
            {row.old !== undefined && row.old !== null ? (
              <>
                <s>{String(row.old)}</s> →{' '}
              </>
            ) : null}
            {String(row.new ?? '—')} {row.ai_suggested !== false ? <AiFieldBadge /> : null}
          </li>
        ))}
      </ul>

      {quotes.length > 0 ? (
        <div className="ai-assist__evidence">
          <button type="button" className="button button--ghost" onClick={onToggleEvidence}>
            {evidenceOpen ? 'Hide evidence' : 'Show evidence'}
          </button>
          {evidenceOpen ? (
            <blockquote>
              {quotes.map((q, idx) => (
                <p key={`${card.id}-ev-${idx}`}>“{q}”</p>
              ))}
            </blockquote>
          ) : null}
        </div>
      ) : null}

      {needsPick ? <MatchPicker candidates={candidates} onPick={onPickOpportunity} /> : null}

      {needsAck ? (
        <div className="ai-assist__acks" role="group" aria-label="Provenance acknowledgements">
          <p className="ai-assist__mini-lede">
            Confirm you reviewed the pasted source before applying.
          </p>
          {requiredAcks.map((key) => (
            <label key={key} className="ai-assist__check">
              <input type="checkbox" checked={acked.has(key)} onChange={() => onToggleAck(key)} />{' '}
              {key === 'provenance_quoted_source' ? 'I reviewed the quoted pasted source' : key}
            </label>
          ))}
        </div>
      ) : null}

      {needsMini ? (
        <div className="ai-assist__mini-form" role="group" aria-label="Clarify fields">
          <p className="ai-assist__mini-lede">Clarify before confirm (mini-form).</p>
          {missing.map((field) => (
            <label key={field} className="ai-assist__label">
              {field}
              <input
                className="ai-assist__input"
                name={field}
                value={clarifyDraft[field] ?? ''}
                onChange={(e) => onClarifyChange(field, e.target.value)}
                placeholder="Required"
              />
            </label>
          ))}
          {checks.map((item) => (
            <label key={item} className="ai-assist__check">
              <input
                type="checkbox"
                checked={checkAcks.has(item)}
                onChange={() => onToggleCheck(item)}
              />{' '}
              Please check: {item}
            </label>
          ))}
          <div className="ai-assist__card-actions">
            <button type="button" className="button button--secondary" onClick={onClarify}>
              Apply clarifications
            </button>
            <button type="button" className="button button--ghost" onClick={onOpenForm}>
              Open full form
            </button>
          </div>
        </div>
      ) : null}

      <div className="ai-assist__card-actions">
        {failed ? (
          <>
            <button type="button" className="button button--secondary" onClick={onOpenForm}>
              Open full form
            </button>
            <button type="button" className="button button--ghost" onClick={onCancel}>
              Cancel card
            </button>
          </>
        ) : !confirmed ? (
          <>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => onDecide('skip')}
            >
              Skip
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={
                card.friction === 'hard_blocked' ||
                createBlocked ||
                needsMini ||
                needsPick ||
                (needsAck && !ackComplete) ||
                card.status === 'cancelled' ||
                card.status === 'skipped'
              }
              onClick={() => onDecide('accept')}
            >
              Confirm
            </button>
            <button type="button" className="button button--ghost" onClick={onCancel}>
              Cancel card
            </button>
          </>
        ) : (
          <button type="button" className="button button--secondary" onClick={onUndo}>
            Undo
          </button>
        )}
      </div>
    </li>
  )
}

function MatchPicker({
  candidates,
  onPick,
}: {
  candidates: string[]
  onPick: (id: string) => void
}) {
  return (
    <div className="ai-assist__match" role="group" aria-label="Pick opportunity">
      <p className="ai-assist__mini-lede">Multiple matches — pick one before Confirm.</p>
      <ul>
        {candidates.map((id) => (
          <MatchCandidate key={id} id={id} onPick={onPick} />
        ))}
      </ul>
    </div>
  )
}

function MatchCandidate({ id, onPick }: { id: string; onPick: (id: string) => void }) {
  const query = useQuery({
    queryKey: ['opportunities', id, 'ai-match'],
    queryFn: () => fetchOpportunity(id),
    staleTime: 60_000,
  })
  const label = query.data
    ? `${query.data.customer_name ?? 'Customer'} — ${query.data.name}`
    : id.slice(0, 8)
  return (
    <li>
      <button type="button" className="button button--secondary" onClick={() => onPick(id)}>
        {label}
      </button>
    </li>
  )
}
